import React from "react"
import { ShoppingCartIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import PriceDisplay from "./PriceDisplay"
import Img from "./Img"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { useCategories } from "../hooks/useCatalogData"
import { useCurrency } from "../store/currency"
import { useCart, cartQuantityOf, addProductsToCart, openCart } from "../store/cart"
import { useStoreAccount, ownsProduct } from "../store/account"
import { PLACEMENTS } from "../store/placements"
import { needsSelection } from "aldoo-ra/Store/extensions"
import { hasPlatformSelection } from "../checkout/cartItems"
import { basePrice, isSoldInCoins, isOwned, isOutOfStock, isRepeatPurchase } from "../utils/productUtils"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { ListProducts, LIST_FIELDS } from "../api"

// a series holds a few books; a long one is still taken whole
const SERIES_MAX = 60

// the covers' fan: the middle one in front and upright, the side ones tilted out behind it (a butterfly)
const FAN = {
  2: [
    { rotate: -9, x: 10, z: 1 },
    { rotate: 9, x: -10, z: 2 },
  ],
  3: [
    { rotate: -14, x: 22, y: 6, z: 1 },
    { rotate: 0, x: 0, y: 0, z: 3, scale: 1.08 },
    { rotate: 14, x: -22, y: 6, z: 2 },
  ],
}

/** Two or three covers of the series fanned out, rising out of the banner's top. */
function Covers({ books, className = "" }) {
  const covers = books.filter((p) => p.original || p.thumb || p.square).slice(0, 3)
  if (covers.length < 2) return null
  // the series' first book in the middle, in front
  const shown = covers.length === 3 ? [covers[1], covers[0], covers[2]] : covers
  const fan = FAN[shown.length]
  return (
    <span className={twMerge("relative flex items-end justify-center shrink-0 pointer-events-none transition-transform duration-300 group-hover:-translate-y-1", className)} aria-hidden="true">
      {shown.map((p, i) => {
        const { rotate, x = 0, y = 0, z, scale = 1 } = fan[i]
        return (
          <span
            key={p.pid}
            className="relative block w-[64px] h-[96px] sm:w-[78px] sm:h-[116px] -mx-2 rounded-[3px] overflow-hidden bg-paper shadow-[0_8px_18px_rgba(0,0,0,0.45)] ring-1 ring-black/10"
            style={{ transform: `translate(${x}px, ${y}px) rotate(${rotate}deg) scale(${scale})`, zIndex: z }}
          >
            <Img src={p.original || p.thumb || p.square} alt="" width={156} height={232} sizes="80px" className="w-full h-full" />
          </span>
        )
      })}
    </span>
  )
}

/**
 * Above a series' books (a category of kind "series" with more than one
 * book): "Get the full series" — the books' regular total struck, the total
 * they sell at now, and "Get now". The whole banner is the button: it adds
 * every book of the series that can go into the cart and is not in it yet
 * (not owned, on sale, nothing to choose first), one of each, and opens the
 * cart. With all of them in the cart already it says so and opens the cart.
 */
export default function SeriesBanner({ categoryId, className = "" }) {
  const t = useT()
  const language = useSiteLanguage()
  const { list: categories } = useCategories()
  const { currency, convert } = useCurrency()
  const cart = useCart()
  const account = useStoreAccount()

  // a series term proper: kind "series", under the "Series" root (whose parent is no series)
  const byId = (id) => categories.find((c) => c.categoryId === id)
  const series = byId(categoryId)
  const isSeries = series?.kind === "series" && (series.parentCategoryIds || []).some((id) => byId(id)?.kind === "series")

  const { result: books } = useAsync(
    async () => {
      const page = await ListProducts({ page: 0, category: categoryId, items_per_page: SERIES_MAX, language, owned_only: false, fields: LIST_FIELDS }).catch(() => null)
      return page?.list || []
    },
    [categoryId, language],
    { enabled: !!isSeries }
  )

  if (!isSeries || !books || books.length < 2) return null

  // what can go into the cart as it is: sold for money, not owned, on sale, nothing to pick first
  const buyable = books.filter((p) => {
    if (p.free_access || !(p.real_money_cost || p.real_price) || isSoldInCoins(p)) return false
    if (!isRepeatPurchase(p) && (isOwned(p) || ownsProduct(account, p.pid))) return false
    return !isOutOfStock(p) && !needsSelection(p) && !hasPlatformSelection(p)
  })
  if (buyable.length < 2) return null
  // only what the cart does not hold yet
  const missing = buyable.filter((p) => !cartQuantityOf(cart, p.pid))

  // the totals in the visitor's currency: the regular prices, and what the books sell at now
  let regular = 0
  let now = 0
  for (const p of missing) {
    const base = basePrice(p)
    if (!base) continue
    const amount = convert(base.amount, base.currency)
    regular += amount
    now += applyDiscount(amount, activeDiscountPercent(p))
  }
  const saves = regular - now > 0.005

  const complete = !missing.length
  const onClick = () => (complete ? openCart() : addProductsToCart(missing, PLACEMENTS.SERIES_BANNER))
  const count =
    missing.length === buyable.length
      ? t("%count% books", missing.length)
      : t("%count% of %total% books — the rest are in your cart", missing.length, buyable.length)

  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        // room above for the covers rising out of it
        "group relative w-full text-left mt-[64px] sm:mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 rounded-2xl px-4 pb-4 pt-0 sm:px-6 sm:py-4 bg-dark text-text_inv hover:bg-dark/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        className
      )}
      data-testid="series-banner"
    >
      <Covers books={books} className="-mt-11 sm:-mt-12 sm:-mb-1 self-center sm:self-auto sm:mx-2" />
      {/* the series and its price on one line: the name and the call on the left, the totals on the right */}
      <span className="flex items-center gap-3 flex-1 min-w-0">
        <span className="flex-1 min-w-0">
          <span className="block text-xs sm:text-sm font-semibold uppercase tracking-wide text-primary truncate">{series.name}</span>
          <span className="block text-lg sm:text-2xl font-bold leading-tight mt-0.5">
            {complete ? t`The whole series is in your cart` : t`Get the full series`}
          </span>
          {!complete && <span className="block text-xs sm:text-sm opacity-70 mt-1">{count}</span>}
        </span>
        {!complete && (
          <span className="flex flex-col items-end shrink-0">
            {saves && <PriceDisplay amount={regular} currency={currency} variant="medium_struck" struck className="opacity-60 text-text_inv" />}
            <PriceDisplay amount={now} currency={currency} variant="medium" className="min-w-0 text-text_inv" />
          </span>
        )}
      </span>
      <span className="inline-flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto h-11 px-6 rounded-full bg-primary text-white font-semibold group-hover:bg-primary_hov transition-colors">
        <ShoppingCartIcon className="w-5 h-5" aria-hidden="true" />
        {complete ? t`Open cart` : t`Get now`}
      </span>
    </button>
  )
}
